import React from 'react';
import PropTypes from 'prop-types';
import { Helmet } from 'react-helmet-async';
import { get, find } from 'lodash';
import md5 from 'md5';
import ReactMarkdown from 'react-markdown';
import scrollTo from 'commons/scrollTo';
import getStoreDetails from 'commons/getStoreDetails';
import {
  setItemInStorage,
  getItemFromStorage,
  removeItemInStorage,
  getPalomaPixelData,
  clearPalomaPixelData,
} from 'commons/localStorage';
import {
  isAfter,
  isBefore,
  differenceInDays,
  parseISO,
  format,
} from 'date-fns';
import { palomaPixelPurchase } from 'commons/analytics';
import { getAnalyticsDataFromCartItem } from 'components/consumer/cart/utils';
import { SET_CHECKOUT_DATA } from 'mocks/mutations';
import IconClose from 'components/icons/iconClose';
import IconError from 'components/icons/iconError';
import HeaderLogo from 'components/consumer/consumer-header/header-logo';
import SimpleFooter from 'components/consumer/consumer-footer/simple-footer';
import ProgressLoader from 'components/essentials/ProgressLoader/ProgressLoader';
import { LUX_USER_DATA, sendLuxUserData } from 'commons/lux';
import { getUserCurrentPageType } from 'hooks/useUserCurrentPageType';
import MobileOnly from 'components/consumer/MobileOnly';
import TabletAndDesktopOnly from 'components/consumer/TabletAndDesktopOnly';
import { SHA256 } from 'crypto-js';
import { useAtom } from 'jotai';
import lastInsightsClickAtom from 'components/consumer/consumer-search/advanced-search/atoms/lastInsightsClickAtom';
import handleInsightsPurchaseEvent from 'components/consumer/consumer-search/algolia-insights-helpers/handleInsightsPurchase';
import EmailForm, { emailFormSchema } from './components/EmailForm';
import Shipping from './components/Shipping';
import StepTitle from './components/StepTitle';
import {
  shippingFormSchema,
  tradeShippingFormSchema,
} from './components/Shipping/components/ShippingForm';
import Payment from './components/Payment';
import Delivery from './components/Delivery';
import Protection from './components/Protection';
import CheckoutProgress from './components/CheckoutProgress';
import OrderSummary from './components/OrderSummary';
import CheckoutLoader from './components/CheckoutLoader';
import { withData } from './withData';
import {
  getPayPalData,
  getApplePayData,
  formatPhoneForEvent,
  getBraintreeClientToken,
} from '../withCheckoutUtils';
import { getCheckoutToken } from '../utils/checkout';
import { validateAddress } from '../utils/addressValidation';

import avo from '../../../../analytics/avo';
import JbEventFBPurchaseProvider from '../JbEventFBPurchaseProvider';

const POST_ORDER_STORAGE = 'posOrderStorage';

const getPageTitle = tabId => {
  const tabs = {
    email: 'Email',
    shipping: 'Shipping',
    delivery: 'Delivery',
    protection: 'Protection',
    payment: 'Billing',
  };

  return tabs[tabId] || '';
};

const getDeliveryEventDetails = shippingGroups => {
  if (!shippingGroups) {
    return {};
  }
  let earliestDate = null;
  let latestDate = null;
  shippingGroups.forEach(shippingGroup => {
    const start = get(shippingGroup, 'delivery_dates.etaDelivery.start', null);
    const end = get(shippingGroup, 'delivery_dates.etaDelivery.end', null);
    if (start) {
      if (!earliestDate || isAfter(new Date(earliestDate), parseISO(start))) {
        earliestDate = start;
      }
    }
    if (end) {
      if (!latestDate || isBefore(new Date(latestDate), parseISO(end))) {
        latestDate = end;
      }
    }
  });
  if (earliestDate && latestDate) {
    const startDate = parseISO(earliestDate);
    const endDate = parseISO(latestDate);
    const today = parseISO(format(new Date(), 'yyyy-MM-dd'));

    return {
      delivery_time_delta: differenceInDays(endDate, startDate),
      delivery_time_earliest: differenceInDays(startDate, today),
      delivery_time_latest: differenceInDays(endDate, today),
    };
  }
  return {};
};

const triggerLuxEvent = actionField => {
  sendLuxUserData(LUX_USER_DATA.PURCHASED);
  sendLuxUserData(LUX_USER_DATA.PURCHASED_CART_VALUE, actionField.revenue);

  // https://stitchindustries.atlassian.net/browse/CON-3005
  sendLuxUserData(LUX_USER_DATA.ORDER_COMPLETED, 1);
  sendLuxUserData(LUX_USER_DATA.ORDER_COMPLETED_REVENUE, actionField.revenue);
};

const triggerSegmentEvent = (actionField /* , cart, products */) => {
  delete actionField.hashedEmail;
};

class Checkout extends React.Component {
  static isShippingChanged = (prevShipping, newShipping) =>
    !(
      newShipping &&
      prevShipping &&
      prevShipping.state === newShipping.state &&
      prevShipping.zipcode === newShipping.zipcode
    );

  static parseError = (errors, defaultMsg = 'Error occurred') => {
    /* CON-1716 */
    let defaultMsgTemp = defaultMsg;
    if (
      /^Zip code .*is not on the list. please call Customer Service/.test(
        defaultMsg
      )
    ) {
      const matches = defaultMsg.match(/(\d+)/);
      defaultMsgTemp = `Zip code ${matches[0]} is outside our shipping range and will result in a higher flat rate shipping fee. Contact Customer Service for further assistance.`;
    }
    if (errors?.[0]?.field === 'customerEmail') {
      return `Error in the email field ${errors[0].message}`;
    }
    return get(errors, '0.message', defaultMsgTemp);
  };

  constructor(props) {
    super(props);

    this.fabricData = null;

    const storeDetails = getStoreDetails();
    const withDataState = this.props.withDataState || {};

    this.state = {
      // server related
      email_address: withDataState.email_address,
      selectedStoreEmployee: withDataState.selectedStoreEmployee,
      shipping: withDataState.shipping,
      payment: {
        selected_tab: storeDetails?.id ? 'point-of-sale' : 'pay-now',
        selected_type: storeDetails?.id ? 'point_of_sale' : 'credit_card',
      },
      deliveryInfo: withDataState.deliveryInfo,
      shippingGroups: withDataState.shippingGroups,

      // common
      orderSummary: withDataState.orderSummary,
      affirm_token: null,

      // ui related
      error: withDataState.errors
        ? Checkout.parseError(withDataState.errors)
        : null,
      affirmOrderInProgress: withDataState.affirmOrderInProgress,
      selected_tab: 'email',
      removeLoading: false,
      validateFormOnLoad: false,
      placeOrderLoading: false,
      analyticsPurchaseEventFired: false,
      shippingGroupsLoading: false,
      disableShippingFormSubmitBtn: false,
      couponCode: null,
      applePayData: { status: 'loading' },
      payPalData: { status: 'loading' },
      posData: withDataState.posData,
      clientAuth: null,
      showShippingSuggestionModal: false,
      shippingAddressSuggestion: null,
      lastInsightsClick: null,
    };
  }

  async componentDidMount() {
    avo.checkoutStepViewed({ step: 1 });

    const affirm_token = getCheckoutToken();
    if (affirm_token) {
      if (this.state.shippingGroups) {
        const affirmOrderResponse = await this.placeOrder({
          selected_type: 'affirm',
          affirm_token,
        });
        if (affirmOrderResponse?.orderId) {
          await this.onOrderSuccess(affirmOrderResponse.orderId);
        }
      } else {
        this.setState({ affirmOrderInProgress: false });
      }
    }
  }

  saveState = () => {
    const newState = {
      email_address: this.state.email_address,
      selectedStoreEmployee: this.state.selectedStoreEmployee,
      newsletter_opt_in: this.state.newsletter_opt_in,
      shipping: this.state.shipping,
      deliveryInfo: this.state.deliveryInfo,
      selected_tab: this.state.selected_tab,
    };

    this.props.client.mutate({
      mutation: SET_CHECKOUT_DATA,
      variables: {
        checkout: newState,
      },
    });
  };

  goToTab = (tab, onTabChange) => {
    this.setState({ selected_tab: tab }, () => {
      scrollTo({ left: 0, top: 0, behavior: 'smooth' });
      if (onTabChange) {
        onTabChange();
      }
      if (tab !== 'email') {
        this.refreshPaymentData();
      }
      if (tab !== 'payment') {
        this.saveState();
      }
      if (tab === 'protection' || tab === 'payment') {
        this.getCartIssues();
        this.getShippingGroups();
      }
      let tabIndex = 1;
      switch (tab) {
        case 'email':
          tabIndex = 1;
          break;
        case 'shipping':
          tabIndex = 2;
          break;
        case 'delivery':
          tabIndex = 3;
          break;
        case 'protection':
          tabIndex = 4;
          break;
        case 'payment':
          tabIndex = 5;
          break;
        default:
          tabIndex = 1;
          break;
      }
      avo.checkoutStepViewed({ step: tabIndex });
    });
  };

  goToPreviousTab = tab => {
    const tabconfig = {
      email: { index: 1 },
      shipping: { index: 2 },
      delivery: { index: 3 },
      protection: { index: 4 },
      payment: { index: 5 },
    };
    const currentTabIndex = tabconfig[this.state.selected_tab].index;
    const selectedTabIndex = tabconfig[tab].index;

    if (selectedTabIndex < currentTabIndex) {
      this.goToTab(tab);
    }
  };

  refreshPaymentData = async () => {
    const { email_address, shipping, clientAuth } = this.state;

    if (
      !email_address ||
      !shipping ||
      !shipping.first_name ||
      !shipping.last_name
    )
      return null;

    // check if any variables are changed
    if (
      !clientAuth ||
      clientAuth.email_address !== email_address ||
      clientAuth.first_name !== shipping.first_name ||
      clientAuth.last_name !== shipping.last_name
    ) {
      const { error, clientToken } = await getBraintreeClientToken(
        this.state,
        this.props.client
      );
      if (error) {
        return this.showError([], error);
      }
      this.setState({
        applePayData: { status: 'loading' },
        payPalData: { status: 'loading' },
        clientAuth: {
          email_address,
          first_name: shipping.first_name,
          last_name: shipping.last_name,
          token: clientToken,
        },
      });
      setTimeout(() => {
        getPayPalData(clientToken).then(payPalData => {
          this.setState({ payPalData });
        });
        getApplePayData(clientToken).then(applePayData => {
          this.setState({ applePayData });
        });
      });
    }

    return null;
  };

  showError = (errors, defaultMsg) => {
    this.setState(
      {
        error: Checkout.parseError(errors, defaultMsg),
      },
      () => {
        scrollTo({ left: 0, top: 0, behavior: 'smooth' });
      }
    );
  };

  closeError = () => {
    this.setState({ error: null });
  };

  // update email manually
  updateEmail = emailAddress => {
    this.setState({ email_address: emailAddress });
  };

  // from email tab
  submitEmail = ({
    email_address,
    newsletter_opt_in,
    selectedStoreEmployee,
  }) => {
    avo.emailAcquired({
      userId_: this.props.userId?.toString() || '',
      email: email_address,
      optedInMarketing: newsletter_opt_in,
      location: getUserCurrentPageType(this.props?.match),
      backendEmail: email_address,
      component: avo.Component.CART,
      talkativeCustomerId: '',
    });
    this.setState(
      { email_address, newsletter_opt_in, selectedStoreEmployee },
      () => {
        avo.checkoutStepCompleted({ step: 1 });
        this.goToTab('shipping');
      }
    );
  };

  // from shipping tab
  updateShipping = shipping => {
    const { shipping: prevShipping } = this.state;
    this.setState({ shipping, disableShippingFormSubmitBtn: false }, () => {
      const isShippingChanged = Checkout.isShippingChanged(
        shipping,
        prevShipping
      );
      this.getShippingGroups(isShippingChanged);
    });
  };

  validateShippingAddress = async shipping => {
    const { showSuggestion, suggestion } = await validateAddress(shipping);
    if (showSuggestion) {
      this.setState({
        showShippingSuggestionModal: true,
        shippingAddressSuggestion: suggestion,
      });
    } else {
      await this.submitShippingForm({ ...this.state.shipping });
    }
  };

  handleSelectRecommendedShipping = async () => {
    await this.submitShippingForm({
      ...this.state.shipping,
      ...this.state.shippingAddressSuggestion,
    });
  };

  submitShippingForm = async shipping => {
    const { shipping: prevShipping } = this.state;
    const isValidZip = await this.validateZipCodeWithState(shipping);

    if (isValidZip) {
      this.setState(
        {
          shipping,
          disableShippingFormSubmitBtn: false,
          showShippingSuggestionModal: false,
        },
        () => {
          const isShippingChanged = Checkout.isShippingChanged(
            shipping,
            prevShipping
          );
          this.getCartIssues();
          this.getShippingGroups(isShippingChanged);
          this.goToTab('delivery');
        }
      );
      avo.checkoutStepCompleted({ step: 2 });
    } else {
      this.setState({ disableShippingFormSubmitBtn: true });
    }
  };

  // from delivery tab
  updateDeliveryInfo = deliveryInfo => {
    this.setState({ deliveryInfo }, this.calculateTotal);
  };

  // from payment tab
  updatePayment = payment => {
    this.setState({ payment });
  };

  getShippingGroups = async isShippingChanged => {
    // prevent duplicate calls
    if (this.state.shippingGroupsLoading) return;

    const { shipping, deliveryInfo } = this.state;
    this.setState({ shippingGroupsLoading: true });
    const res = await this.props.checkoutUtilActions.getShippingGroups(
      shipping,
      deliveryInfo
    );
    this.setState({ shippingGroupsLoading: false });
    if (res && res.errors) {
      this.showError(res.errors);
    } else {
      // if shipping is changed, use new delivery info
      const newDeliveryInfo = isShippingChanged ? {} : { ...deliveryInfo };
      this.setState(
        {
          shippingGroups: res.shippingGroups,
          deliveryInfo: { ...res.deliveryInfo, ...newDeliveryInfo },
        },
        this.calculateTotal
      );
    }
  };

  getCartIssues = async () => {
    const { shipping } = this.state;
    const { cart, issues } = await this.props.getCartIssues(shipping?.zipcode);
    return { cart, issues };
  };

  removeFromCart = async items => {
    this.setState({ removeLoading: true });
    await Promise.all(
      items.map(async item => {
        await this.props.checkoutUtilActions.removeFromCart(item);
      })
    );
    await Promise.all([this.getCartIssues(), this.getShippingGroups()]);
    this.setState({ removeLoading: false });
  };

  calculateTotal = async () => {
    const { email_address, shipping, deliveryInfo } = this.state;
    const orderSummary = await this.props.checkoutUtilActions.calculateTotal({
      email_address,
      shipping,
      deliveryInfo,
    });

    if (orderSummary && orderSummary.errors) {
      return this.showError(orderSummary.errors);
    }

    this.setState({ orderSummary });

    return orderSummary;
  };

  validateZipCodeWithState = async address => {
    const { state, zipcode } = address;
    const result = await this.props.checkoutUtilActions.validateZipWithState(
      zipcode,
      state
    );

    if (!result || !result.isValid) {
      this.goToTab('shipping', () => {
        this.setState({ validateFormOnLoad: false });
      });
      this.showError(
        [],
        get(result, 'errors.0.message', 'Please enter valid zip code.')
      );
    } else {
      this.closeError();
    }
    return result && result.isValid;
  };

  validateCheckout = async () => {
    const { isTradeUser } = this.props;
    const { email_address, shipping } = this.state;
    // validate email form
    try {
      await emailFormSchema.validate({ email_address });
    } catch (e) {
      this.setState({ validateFormOnLoad: true });
      this.goToTab('email', () => {
        this.setState({ validateFormOnLoad: false });
      });
      return false;
    }

    // validate shipping form
    try {
      if (isTradeUser) {
        await tradeShippingFormSchema.validate(shipping);
      } else {
        await shippingFormSchema.validate(shipping);
      }
    } catch (e) {
      this.setState({ validateFormOnLoad: true });
      this.goToTab('shipping', () => {
        this.setState({ validateFormOnLoad: false });
      });
      return false;
    }

    return true;
  };

  placeOrder = async payment => {
    const {
      email_address,
      shipping,
      deliveryInfo,
      clientAuth,
      selectedStoreEmployee,
    } = this.state;

    const isValid = await this.validateCheckout();

    if (!isValid) {
      return null;
    }
    avo.checkoutStepCompleted({ step: 5 });

    this.setState({ payment, error: null, placeOrderLoading: true });

    const { cart, issues } = await this.getCartIssues();
    if (issues.length) {
      this.setState({ placeOrderLoading: false, affirmOrderInProgress: false });
      return null;
    }

    // get latest order summary
    const orderSummary = await this.calculateTotal();

    // show an error to user in-case the credit cards amounts are not equalling the latest order summary
    if (
      payment.selected_type === 'credit_card' &&
      payment.credit_cards.length === 2
    ) {
      if (
        orderSummary.grand_total !==
        payment.credit_cards[0].amount + payment.credit_cards[1].amount
      ) {
        this.setState({
          placeOrderLoading: false,
          affirmOrderInProgress: false,
        });
        this.showError(
          [],
          'Payable amount is not same as the Total. Please check the payment amount.'
        );
        return null;
      }
    }

    // check for duplicate orders in case of failure
    if (payment.selected_type === 'point_of_sale') {
      const prevOrder = getItemFromStorage(POST_ORDER_STORAGE);
      if (
        prevOrder &&
        prevOrder.orderId &&
        prevOrder.customerId &&
        prevOrder.zipcode === shipping.zipcode &&
        prevOrder.email_address === email_address &&
        prevOrder.grand_total === orderSummary.grand_total &&
        prevOrder.cart ===
          cart
            .map(item => `${item.product_id}:${item.sku}:${item.quantity}`)
            .join('|')
      ) {
        this.setState({
          placeOrderLoading: false,
          affirmOrderInProgress: false,
          orderId: prevOrder.orderId,
          customerId: prevOrder.customerId,
        });

        return { orderId: prevOrder.orderId, customerId: prevOrder.customerId };
      }
    }

    const storeDetails = getStoreDetails();
    const {
      errors,
      orderId,
      customerId,
    } = await this.props.checkoutUtilActions.placeOrder({
      email_address,
      entered_by: selectedStoreEmployee,
      retail_id: storeDetails?.id,
      shipping,
      payment,
      deliveryInfo,
      orderSummary,
      clientAuth,
      cart: cart.map(item => ({
        name: item.name,
        cart_item_id: item.id,
        product_id: item.product_id,
        quantity: item.quantity,
        sku: item.sku,
        cart_item_errors: item.cart_item_errors,
      })),
    });

    if (errors) {
      this.setState({ clientAuth: null });
      this.showError(errors, 'Failed to place the order. Please try again.');
    }

    this.setState({
      orderId,
      customerId,
      placeOrderLoading: false,
      affirmOrderInProgress: false,
    });

    if (payment.selected_type === 'point_of_sale') {
      setItemInStorage(POST_ORDER_STORAGE, {
        orderId,
        customerId,
        email_address,
        zipcode: shipping.zipcode,
        grand_total: orderSummary.grand_total,
        cart: cart
          .map(item => `${item.product_id}:${item.sku}:${item.quantity}`)
          .join('|'),
      });
    }

    return { orderId, customerId };
  };

  onOrderSuccess = async orderIdFromPlaceOrder => {
    // Use the orderId passed from placeOrder instead of relying on state
    // since setState is asynchronous and the state might not be updated yet
    const orderId = orderIdFromPlaceOrder || this.state.orderId;

    const {
      payment,
      shipping,
      customerId,
      email_address,
      deliveryInfo,
      orderSummary,
      shippingGroups,
    } = this.state;

    const { cartData: cart, checkoutSummaryData } = this.props;

    const storeDetails = getStoreDetails();
    const dateNow = new Date(Date.now());
    const earliestDelivery = new Date(
      shippingGroups[0].delivery_dates.etaDelivery.start
    );
    const latestDelivery = new Date(
      shippingGroups[0].delivery_dates.etaDelivery.end
    );
    const deliveryTimeEarliest = Math.ceil(
      (earliestDelivery.getTime() - dateNow.getTime()) / (1000 * 60 * 60 * 24)
    );
    const deliveryTimeLatest = Math.ceil(
      (latestDelivery.getTime() - dateNow.getTime()) / (1000 * 60 * 60 * 24)
    );
    const deliveryTimeDelta = deliveryTimeLatest - deliveryTimeEarliest;

    const orderDiscount =
      orderSummary?.discount || orderSummary?.base_discount_amount;

    if (!this.fabricData) {
      const fabricsData = await this.props.checkoutUtilActions.getFabricsData();
      this.fabricsData = fabricsData.reduce((acc, cur) => {
        acc[cur.option_value_id] = cur;
        return acc;
      }, {});
    }

    const city = SHA256(
      shipping?.city?.replace(/[^a-zA-Z]|\s+/g, '').toLowerCase()
    ).toString();

    const state = SHA256(
      shipping?.state?.replace(/[^a-zA-Z]|\s+/g, '').toLowerCase()
    ).toString();

    const zip = SHA256(
      shipping?.zipcode?.replace(/[^a-zA-Z]|\s+/g, '').toLowerCase()
    ).toString();

    const giftCouponTotal = orderSummary?.applied_coupons.reduce(
      (total, coupon) =>
        total + (coupon?.type === 'gift' && coupon?.amount_used > 0)
          ? parseInt(coupon?.amount_used, 10)
          : 0,
      0
    );

    avo.orderCompleted({
      userId_: customerId?.toString() || '',
      cartId: checkoutSummaryData.checkoutSummary?.cart_token,
      storeId: storeDetails?.id ? parseInt(storeDetails.id, 10) : 1,
      products: cart.map(item => {
        let variant = '';
        item.options.forEach((option, index) => {
          if (index === 0) {
            variant += `${option.name}`;
          } else {
            variant += `, ${option.name}`;
          }
        });
        const cartProduct = getAnalyticsDataFromCartItem(item);
        const fabricOption = find(item.options, { optionTypeId: 17 });
        if (
          !cartProduct.pickLater &&
          fabricOption &&
          this.fabricsData[fabricOption.id]
        ) {
          cartProduct.fabricColor = this.fabricsData[
            fabricOption.id
          ].option_color;
          cartProduct.fabricName = this.fabricsData[
            fabricOption.id
          ].option_value;
        }
        cartProduct.is_clearance = parseInt(item.stock_discount, 10) > 0;

        return {
          productId: parseInt(item.product_id, 10),
          category: item.product.type.name,
          isClearance: item.is_clearance ? item.is_clearance : false,
          name: item.name,
          price: parseInt(parseFloat(item.price), 10),
          fullPrice: parseInt(parseFloat(item.price_pre_discount), 10),
          quantity: parseInt(item.quantity, 10),
          sku: [item.product.sku, item.sku].filter(Boolean).join(''),
          variant,
          fabricName: cartProduct.fabricName,
          fabricColor: cartProduct.fabricColor,
          currency: 'USD',
          shippingMethod: 'Curbside',
          shippingCarrier: 'Custom',
          isFlashSale: !!item?.flash_sale,
          googleProductCategory: item?.product?.googleProductCategory || '',
        };
      }),
      affiliation: storeDetails ? storeDetails.name : 'Online',
      coupon: this.state.couponCode || '',
      deliveryTimeDelta,
      deliveryTimeEarliest,
      deliveryTimeLatest,
      discount: parseFloat(orderDiscount || 0),
      orderId: orderId?.toString(),
      paymentMethod:
        payment.selected_type === 'credit_card'
          ? 'Credit Card'
          : payment.selected_type === 'affirm'
          ? 'Affirm'
          : payment.selected_type === 'paypal'
          ? 'PayPal'
          : 'Apple Pay',
      promotionName: 'n/a',
      phone: parseInt(shipping.phone_number.replace(/[)( -]/g, ''), 10),
      shipping: parseFloat(orderSummary?.total_shipping || 0),
      tax: parseFloat(orderSummary?.tax_amount || 0),
      total: parseFloat(orderSummary?.grand_total || 0),
      revenue: parseFloat(
        (orderSummary?.cart_subtotal ?? 0) -
          orderDiscount +
          (orderSummary?.protectionSubtotal ?? 0) || 0
      ),
      subtotal: parseFloat(orderSummary?.cart_subtotal || 0),
      currency: 'USD',
      city,
      state,
      zip,
      talkativeCustomerId: '',
      giftAmount: giftCouponTotal,
    });

    const { lastInsightsClick } = this.props;
    handleInsightsPurchaseEvent({ hit: lastInsightsClick, cart });

    if (payment.selected_type === 'point_of_sale') {
      removeItemInStorage(POST_ORDER_STORAGE);
    }

    setItemInStorage('orderThankYou', {
      orderId,
      orderSummary,
      shipping,
      payment,
      deliveryInfo,
      shippingGroups,
      cartData: cart,
    });

    const paloma = getPalomaPixelData();
    clearPalomaPixelData();

    if (paloma) {
      palomaPixelPurchase({
        subscriber_id: paloma.paloma_subscriber_id,
        conversation_id: paloma.paloma_conversation_id,
        total: parseFloat(orderSummary?.grand_total || 0), // in USD cents
        order_id: orderId, // Joybird order id
        email: email_address,
        products: cart.map(item => {
          let variant = '';
          item.options.forEach((option, index) => {
            if (index === 0) {
              variant += `${option.name}`;
            } else {
              variant += `, ${option.name}`;
            }
          });
          return {
            product_id: item.product_id,
            category: item.product.type.name,
            name: item.name,
            price: parseFloat(item.price),
            quantity: parseInt(item.quantity, 10),
            sku: [item.product.sku, item.sku].filter(Boolean).join(''),
            variant,
          };
        }),
      });
    }
    this.props.history.push('/thank-you');

    await this.triggerAnalyticsEvent(orderId, cart, customerId);
  };

  onSuccessfulCoupon = couponCode => {
    this.setState({ couponCode });
  };

  async triggerAnalyticsEvent(orderId, cart) {
    if (this.state.analyticsPurchaseEventFired) {
      return;
    }
    this.setState({ analyticsPurchaseEventFired: true });

    const {
      email_address,
      shipping,
      orderSummary,
      shippingGroups,
      payment,
    } = this.state;
    const {
      delivery_time_delta,
      delivery_time_earliest,
      delivery_time_latest,
    } = getDeliveryEventDetails(shippingGroups);
    const actionField = {
      affiliation: 'Online',
      couponCode: this.state.couponCode || '',
      hashedEmail: email_address ? md5(email_address) : '',
      email: email_address,
      phone: formatPhoneForEvent(shipping.phone_number),
      orderId,
      revenue: orderSummary.grand_total,
      tax: orderSummary.tax_amount,
      shipping: orderSummary.total_shipping,
    };

    const products = [];
    let isClearanceProduct = false;
    cart.forEach(cartItem => {
      isClearanceProduct =
        isClearanceProduct || parseInt(cartItem.stock_discount, 10) > 0;
      const cartProduct = getAnalyticsDataFromCartItem(cartItem);
      const fabricOption = find(cartItem.options, { optionTypeId: 17 });
      if (
        !cartProduct.pickLater &&
        fabricOption &&
        this.fabricsData[fabricOption.id]
      ) {
        cartProduct.fabricColor = this.fabricsData[
          fabricOption.id
        ].option_color;
      }
      cartProduct.is_clearance = parseInt(cartItem.stock_discount, 10) > 0;
      products.push(cartProduct);
    });

    const heapActionField = {
      ...actionField,
      delivery_time_delta,
      delivery_time_earliest,
      delivery_time_latest,
      payment_type: payment.selected_type
        .split('_')
        .map(x => x.replace(/(?:^|\s)\S/g, match => match.toUpperCase()))
        .join(' '),
      promotion_name:
        orderSummary.promotionName &&
        !orderSummary.promotionName.includes('Non-Sale')
          ? orderSummary.promotionName
          : '',
    };
    triggerSegmentEvent(heapActionField, cart, products);
    triggerLuxEvent(heapActionField, cart);
  }

  render() {
    const {
      cartIssues,
      cartIssueType,
      isTradeUser,
      cartData: cart,
    } = this.props;

    // render loading
    if (
      this.state.isRehydrating ||
      this.state.affirmOrderInProgress ||
      !cart.length
    ) {
      return <CheckoutLoader isCartEmpty={!cart.length} />;
    }

    const {
      error,
      shipping,
      deliveryInfo,
      shippingGroups,
      email_address,
      newsletter_opt_in,
      orderSummary,
      selected_tab,
      placeOrderLoading,
      validateFormOnLoad,
      shippingGroupsLoading,
    } = this.state;

    const orderSummaryComponent = (
      <OrderSummary
        cart={cart}
        orderSummary={orderSummary}
        deliveryInfo={deliveryInfo}
        shippingGroups={shippingGroups}
        getCartIssues={this.getCartIssues}
        calculateTotal={this.calculateTotal}
        onSuccessfulCoupon={this.onSuccessfulCoupon}
      />
    );

    return (
      <>
        <Helmet>
          <title>{`Checkout - ${getPageTitle(
            this.state.selected_tab
          )} | Joybird`}</title>

          <script src="https://js.braintreegateway.com/web/3.79.1/js/client.min.js" />
          <script src="https://js.braintreegateway.com/web/3.79.1/js/apple-pay.min.js" />
          <script src="https://js.braintreegateway.com/web/3.79.1/js/paypal-checkout.min.js" />
        </Helmet>

        <div
          data-xc="checkout"
          className="checkout component desktop checkout-component-desktop m-auto min-h-[calc(100vh_-_233px)] md:min-h-[calc(100vh_-_186px)] [&_span]:!font-sans [&_input:-webkit-autofill]:![-webkit-box-shadow:0_0_0_30px_#fff_inset] [&_input:-webkit-autofill]:hover:![-webkit-box-shadow:0_0_0_30px_#fff_inset] [&_input:-webkit-autofill]:focus:![-webkit-box-shadow:0_0_0_30px_#fff_inset] [&_input:-webkit-autofill]:active:![-webkit-box-shadow:0_0_0_30px_#fff_inset] [&_input[type='number']::-webkit-inner-spin-button]:!appearance-none [&_input[type='number']::-webkit-inner-spin-button]:!m-0"
        >
          <div className="md:flex md:flex-row md:flex-[1_0_auto]">
            <div className="CheckoutStages__content items-center flex flex-[1_1_0] flex-col mr-auto max-w-[60rem] pt-0 px-0 pb-4 w-full min-[500px]:pt-0 min-[500px]:px-8 min-[500px]:pb-8 lg:pt-4 lg:pr-8 lg:pb-20 lg:pl-16 xl:pl-16">
              <div className="w-full flex flex-col min-[1440px]:flex-row">
                <div className="mx-0 my-4 max-[500px]:px-4 max-[500px]:py-0 lg:flex lg:flex-[0_0_auto] lg:justify-center lg:m-0 lg:w-[9.5rem] xl:justify-start">
                  <TabletAndDesktopOnly>
                    <HeaderLogo className="max-[767px]:hidden" />
                  </TabletAndDesktopOnly>

                  <MobileOnly>
                    <div className="md:hidden">{orderSummaryComponent}</div>
                  </MobileOnly>
                </div>

                {!this.props?.singlePage && (
                  <CheckoutProgress
                    tabs={this.props.tabs}
                    goToTab={this.goToTab}
                    selectedTab={this.state.selected_tab}
                  />
                )}
              </div>

              <JbEventFBPurchaseProvider
                customerId={this.state.customerId}
                orderId={this.state.orderId}
                cart={cart}
                orderSummary={this.state.orderSummary}
                shipping={this.state.shipping}
              />
              <div className="w-full min-[1440px]:flex-[1_0_auto] min-[1440px]:pl-[9.5rem] max-[500px]:p-[0_1rem]">
                {error && (
                  <div className="text-base w-full flex mx-0 my-4 relative px-4 py-3 items-center font-bold text-red border border-solid border-red [&_a]:text-red [&_a]:underline [&_svg]:mr-2.5">
                    <IconError width={16} height={16} />
                    <ReactMarkdown className="pr-6">{error}</ReactMarkdown>
                    <span
                      role="button"
                      tabIndex={0}
                      className="absolute right-2 top-3"
                      onClick={this.closeError}
                    >
                      <IconClose />
                    </span>
                  </div>
                )}

                {cartIssues && cartIssues.length ? (
                  <div className="text-base w-full flex mx-0 my-4 relative px-4 py-3 items-center font-bold text-red border border-solid border-red [&_a]:text-red [&_a]:underline [&_svg]:mr-2.5">
                    <div>
                      <IconError width={16} height={16} />
                    </div>
                    <div className="[&_svg]:text-gray [&_svg]:w-4 [&_svg]:h-auto">
                      The following products are not
                      {cartIssueType === 'OUT_OF_STOCK'
                        ? ' in stock. '
                        : ' available at the moment. '}
                      <TabletAndDesktopOnly>
                        <div className="max-[767px]:hidden">
                          We apologize for the inconvenience, as we know how
                          hard it is to find the perfect piece.
                        </div>
                      </TabletAndDesktopOnly>
                      {cartIssues.map(item => (
                        <div
                          key={item.name}
                          className="text-gray mt-[.1rem] first:mt-[.325rem]"
                        >
                          {item.name}
                        </div>
                      ))}
                      <div
                        role="button"
                        tabIndex={0}
                        className="cursor-pointer mt-[.2rem] text-brand"
                        onClick={() => this.removeFromCart(cartIssues)}
                      >
                        Please remove to continue &nbsp;
                        {this.state.removeLoading && (
                          <ProgressLoader size={12} />
                        )}
                      </div>
                    </div>
                  </div>
                ) : null}

                {selected_tab === 'email' && (
                  <EmailForm
                    orderSummary={orderSummary}
                    email_address={email_address}
                    newsletter_opt_in={newsletter_opt_in}
                    updateEmail={this.updateEmail}
                    onSubmit={this.submitEmail}
                    validateFormOnLoad={validateFormOnLoad}
                  />
                )}

                {this.props?.singlePage && selected_tab !== 'email' && (
                  <StepTitle
                    number="1"
                    onClick={() => this.goToPreviousTab('email')}
                  >
                    Email
                  </StepTitle>
                )}

                {selected_tab === 'shipping' && (
                  <Shipping
                    shipping={shipping}
                    updateShipping={this.updateShipping}
                    submitShippingForm={this.submitShippingForm}
                    validateFormOnLoad={validateFormOnLoad}
                    disableSubmitBtn={this.state.disableShippingFormSubmitBtn}
                    isTradeUser={isTradeUser}
                    showShippingSuggestionModal={
                      this.state.showShippingSuggestionModal
                    }
                    closeShippingSuggestionModal={() => {
                      this.setState({ showShippingSuggestionModal: false });
                    }}
                    validateShippingAddress={this.validateShippingAddress}
                    handleSelectRecommendedShipping={
                      this.handleSelectRecommendedShipping
                    }
                    shippingAddressSuggestion={
                      this.state.shippingAddressSuggestion
                    }
                  />
                )}

                {this.props?.singlePage && selected_tab !== 'shipping' && (
                  <StepTitle
                    number="2"
                    onClick={() => this.goToPreviousTab('shipping')}
                  >
                    Shipping Information
                  </StepTitle>
                )}

                {selected_tab === 'delivery' && (
                  <Delivery
                    goToTab={this.goToTab}
                    deliveryInfo={deliveryInfo}
                    shippingGroups={shippingGroups}
                    shippingGroupsLoading={shippingGroupsLoading}
                    hasProtectionNext={
                      !!this.props.tabs.find(tab => tab.key === 'protection')
                    }
                    updateDeliveryInfo={this.updateDeliveryInfo}
                    avoCheckoutStepCompleted={avo.checkoutStepCompleted}
                    cart={cart}
                    shipping={shipping}
                  />
                )}

                {this.props?.singlePage && selected_tab !== 'delivery' && (
                  <StepTitle
                    number="3"
                    onClick={() => this.goToPreviousTab('delivery')}
                  >
                    Delivery Information
                  </StepTitle>
                )}

                {selected_tab === 'protection' && (
                  <Protection
                    cart={cart}
                    goToTab={this.goToTab}
                    avoCheckoutStepCompleted={avo.checkoutStepCompleted}
                    getCartIssues={this.getCartIssues}
                    calculateTotal={this.calculateTotal}
                    client={this.props.client}
                  />
                )}

                {this.props?.singlePage && selected_tab !== 'protection' && (
                  <StepTitle
                    number="4"
                    onClick={() => this.goToPreviousTab('protection')}
                  >
                    Protection
                  </StepTitle>
                )}

                {selected_tab === 'payment' && (
                  <Payment
                    cart={cart}
                    checkout={this.state}
                    goToTab={this.goToTab}
                    isTradeUser={isTradeUser}
                    placeOrder={this.placeOrder}
                    showError={this.showError}
                    getCartIssues={this.getCartIssues}
                    updatePayment={this.updatePayment}
                    onOrderSuccess={this.onOrderSuccess}
                    calculateTotal={this.calculateTotal}
                    placeOrderLoading={placeOrderLoading}
                    applePayData={this.state.applePayData}
                    payPalData={this.state.payPalData}
                    posData={this.state.posData}
                    history={this.props.history}
                  />
                )}
                {this.props?.singlePage && selected_tab !== 'payment' && (
                  <StepTitle number="5">Billing</StepTitle>
                )}
              </div>
            </div>

            <TabletAndDesktopOnly>
              <div className="max-[767px]:hidden CheckoutStages__sidebar bg-gray-light9 [border-left:1px_solid_#e5e5e5] text-gray-light1 px-4 py-8 min-[500px]:px-8 min-[500px]:py-12 md:max-w-[40rem] md:min-w-[19rem] md:px-6 md:py-[5.5rem] md:w-[38%] lg:pr-12 xl:pr-16 xl:pl-16">
                {orderSummaryComponent}
              </div>
            </TabletAndDesktopOnly>
          </div>
        </div>
        <SimpleFooter />
      </>
    );
  }
}

const CheckoutWithInsights = props => {
  const [lastInsightsClick] = useAtom(lastInsightsClickAtom);
  return <Checkout {...props} lastInsightsClick={lastInsightsClick} />;
};

Checkout.propTypes = {
  cartData: PropTypes.array,
  cartIssues: PropTypes.array,
  cartIssueType: PropTypes.string,
  checkoutSummaryData: PropTypes.shape({
    applePayData: PropTypes.object,
    cart: PropTypes.object,
    checkoutSummary: PropTypes.shape({
      cart_token: PropTypes.string,
    }),
    deliveryInfo: PropTypes.object,
    email: PropTypes.string,
    newsletterOptIn: PropTypes.bool,
    payment: PropTypes.object,
    payPalData: PropTypes.object,
    protection: PropTypes.object,
    shipping: PropTypes.object,
  }),
  checkoutUtilActions: PropTypes.shape({
    calculateTotal: PropTypes.func,
    getFabricsData: PropTypes.func,
    getShippingGroups: PropTypes.func,
    placeOrder: PropTypes.func,
    removeFromCart: PropTypes.func,
    validateZipWithState: PropTypes.func,
  }),
  client: PropTypes.shape({
    get: PropTypes.func,
    mutate: PropTypes.func,
  }),
  getCartIssues: PropTypes.func,
  history: PropTypes.shape({
    push: PropTypes.func,
  }),
  isTradeUser: PropTypes.bool,
  tabs: PropTypes.array,
  withDataState: PropTypes.shape({
    data: PropTypes.object,
    error: PropTypes.object,
    loading: PropTypes.bool,
  }),
  match: PropTypes.shape({
    params: PropTypes.shape({
      id: PropTypes.string,
    }),
  }),
  singlePage: PropTypes.bool,
  userId: PropTypes.number,
};

export default withData(CheckoutWithInsights);
